import { type BookieOdds, useBetslipContext } from '@cx-web/feature-betslip/context';
// providers
import { useLabelActions } from '@cx-web/feature-i18n';
// services
import { buildBetSlipSignUpEvent, trackBetslipEvent } from '@cx-web/feature-tracking/betslip';
import { Button } from '@fpsm/cxy-components/Button';

import ClickoutBetslip from '../icons/clickout';
// styles
import styles from '../style/bookie-selection.module.css';
import FormatCurrency from '../util/format-currency';

import BookieLogo, { LOGO_SIZE } from './bookie-logo';
import { StakeInput } from './stake-input';

const BookieSelection = ({
  selected,
  selectedBookie,
}: {
  selected?: boolean;
  selectedBookie: BookieOdds;
}): React.JSX.Element => {
  const labelActions = useLabelActions();

  const { bookmakers, getOdds, handleClickoutToPlaceBet, selectBookie, stake, userOddsFormat } = useBetslipContext();

  const bookie = bookmakers[`${selectedBookie.bookmakerCode}`];

  const returns = (() => {
    const r = selectedBookie.oddsDecimal * stake;
    return r.toFixed(r > 100 ? 0 : 2);
  })();

  function renderStake() {
    if (selected) {
      return <StakeInput returns={<FormatCurrency value={returns} />} stake={stake} />;
    }

    return (
      <>
        <div className={styles.sectionWrapper}>
          <div className={styles.title}>{labelActions.get('STAKE')}:</div>
          <div className={styles.number}>
            <FormatCurrency value={stake} />
          </div>
        </div>
        <div className={styles.sectionWrapper}>
          <div className={styles.title}>{labelActions.get('RETURNS')}:</div>
          <div className={styles.number}>
            <FormatCurrency value={returns} />
          </div>
        </div>
      </>
    );
  }

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    trackBetslipEvent(buildBetSlipSignUpEvent(selectedBookie.bookmakerCode));
    handleClickoutToPlaceBet(selectedBookie);
  };

  function renderButton(): React.JSX.Element {
    if (selected) {
      return (
        <Button className={styles.button} disabled={!stake} onClick={handleClick} variant="secondary">
          <ClickoutBetslip />
          {labelActions.get('BET')}
        </Button>
      );
    }

    return (
      <Button className={styles.signUpButton} onClick={handleClick} variant="secondary">
        <ClickoutBetslip />
        {labelActions.get('SIGN_UP')}
      </Button>
    );
  }

  const handleBookieSelectionClick = () => {
    if (!selected && typeof selectedBookie.index !== 'undefined') selectBookie(selectedBookie.index);
  };

  return (
    <div className={[styles.bookieSelection, selected ? styles.selected : ''].join(' ')} role="button">
      <BookieLogo
        bookmakerName={bookie?.bookmakerName}
        brandColour={bookie?.brandColour}
        className={styles.bookieLogo}
        logoUrl={bookie?.logoSmall}
        onClick={handleBookieSelectionClick}
        size={LOGO_SIZE.SMALL}
      />
      <div className={styles.sectionWrapper}>
        <div className={styles.title}>{labelActions.get('ODDS')}:</div>
        <div className={styles.odds}>{getOdds(selectedBookie, false, userOddsFormat)}</div>
      </div>
      {renderStake()}
      {renderButton()}
    </div>
  );
};

export default BookieSelection;
